import type { SelectedStep } from '~/server/logic/prisma/step-select';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import { match } from 'ts-pattern';

export function compareSteps(step1: SelectedStep, step2: SelectedStep): boolean {
  // Only compare the actual content of steps, not their order/position
  return step1.description === step2.description && step1.mode === step2.mode && step1.skip === step2.skip;
}

export function operationListsAreEqual(ops1: SelectedOperation[], ops2: SelectedOperation[]): boolean {
  return ops1.length === ops2.length && ops1.every((op, index) => compareOperations(op, ops2[index]));
}

export function compareOperations(op1: SelectedOperation, op2: SelectedOperation): boolean {
  if (op1.type !== op2.type) {
    return false;
  }

  if (op1.skip !== op2.skip) {
    return false;
  }

  return match(op1.type)
    .with(
      'ACTION',
      () =>
        compareNullableStrings(op1.actionOp?.targetV2?.locator, op2.actionOp?.targetV2?.locator) &&
        compareNullableStrings(op1.actionOp?.value, op2.actionOp?.value) &&
        compareNullableStrings(op1.actionOp?.action, op2.actionOp?.action),
    )
    .with('NAVIGATION', () => compareNullableStrings(op1.navigationOp?.value, op2.navigationOp?.value))
    .with(
      'ASSERTION',
      () =>
        compareNullableStrings(op1.assertionOp?.targetV2?.locator, op2.assertionOp?.targetV2?.locator) &&
        compareNullableStrings(op1.assertionOp?.value, op2.assertionOp?.value),
    )
    .with('AI_ASSERTION', () => compareNullableStrings(op1.aiAssertionOp?.assertion, op2.aiAssertionOp?.assertion))
    .with('WAIT', () => op1.waitOp?.timeoutInMillis === op2.waitOp?.timeoutInMillis)
    .with('CODE_BLOCK', () => op1.codeBlockOp?.code.trim() === op2.codeBlockOp?.code.trim())
    .with(
      'DEFINE_VARIABLE',
      () =>
        op1.defineVariableOp?.name === op2.defineVariableOp?.name &&
        op1.defineVariableOp?.value === op2.defineVariableOp?.value &&
        op1.defineVariableOp?.type === op2.defineVariableOp?.type,
    )
    .with(
      'RECEIVE_EMAIL',
      () =>
        op1.receiveEmailOp?.toEmailAddressType === op2.receiveEmailOp?.toEmailAddressType &&
        op1.receiveEmailOp?.toEmailAddressValue === op2.receiveEmailOp?.toEmailAddressValue &&
        op1.receiveEmailOp?.variableExtraction?.extractionType ===
          op2.receiveEmailOp?.variableExtraction?.extractionType &&
        op1.receiveEmailOp?.variableExtraction?.variableName === op2.receiveEmailOp?.variableExtraction?.variableName &&
        op1.receiveEmailOp?.variableExtraction?.customExtractionPrompt ===
          op2.receiveEmailOp?.variableExtraction?.customExtractionPrompt,
    )
    .with('UPLOAD', () => {
      if (op1.uploadOp?.uploadedFiles.length !== op2.uploadOp?.uploadedFiles.length) {
        return false;
      }
      return (
        op1.uploadOp?.uploadedFiles.every((file1) =>
          op2.uploadOp?.uploadedFiles.some((file2) => file1.storageKey === file2.storageKey),
        ) ?? false
      );
    })
    .with(
      'AUTH_STATE',
      () =>
        op1.authStateOp?.actionType === op2.authStateOp?.actionType &&
        op1.authStateOp?.testAccountId === op2.authStateOp?.testAccountId,
    )
    .with(
      'SPECIAL_LOGIN',
      () =>
        op1.specialLoginOp?.type === op2.specialLoginOp?.type &&
        op1.specialLoginOp?.testAccountId === op2.specialLoginOp?.testAccountId,
    )
    .with(
      'AI_ACTION',
      () =>
        compareNullableStrings(op1.aiActionOp?.prompt, op2.aiActionOp?.prompt) &&
        op1.aiActionOp?.model === op2.aiActionOp?.model,
    )
    .with('AI', () =>
      // Handle legacy AI operations (deprecated, but still supported for backward compatibility)
      compareNullableStrings(
        (op1 as { aiActionOp?: { prompt?: string | null } }).aiActionOp?.prompt,
        (op2 as { aiActionOp?: { prompt?: string | null } }).aiActionOp?.prompt,
      ),
    )
    .with('FETCH_REQUEST', () => op1.fetchRequestOp?.url === op2.fetchRequestOp?.url)
    .with('COMPONENT_CALL', () => op1.componentCallOp?.componentId === op2.componentCallOp?.componentId)
    .with('CONDITIONAL', () => {
      return op1.conditionalOp?.conditionPrompt === op2.conditionalOp?.conditionPrompt;
    })
    .with('END_TEST', () => op1.endTestOp?.passTest === op2.endTestOp?.passTest)
    .exhaustive();
}

function compareNullableStrings(s1?: string | null, s2?: string | null): boolean {
  const isNullishOrEmpty = (value?: string | null) => value === null || value === undefined || value === '';

  return (isNullishOrEmpty(s1) && isNullishOrEmpty(s2)) || s1 === s2;
}
